import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import {
  AnswerSources,
  ApiActionLogMessage,
  ApiActionOutput,
  ApiActionSearchChunk,
  ApiActionSearchHit,
  ApiActionType,
  ApiChatStatus,
  ApiStreamingJSONAnswerSource,
  FeedLayoutType,
  FlatAction,
  ICompositeResult,
  IsDebugModeEnabledByQuestion,
  NextStep,
  ChatHistory,
  SearchArgs
} from "../../types/entities";

export const AiModels = [
  "gpt-4.1",
  "gpt-4.1-2025-04-14",
  "gpt-4.1-mini",
  "gpt-4.1-mini-2025-04-14",
  "gpt-4.1-nano",
  "gpt-4.1-nano-2025-04-14",
  "gpt-4o",
  "gpt-4o-2024-05-13",
  "gpt-4o-mini",
  "gpt-4o-mini-2024-07-18",
  "gpt-4-turbo",
  "gpt-4-turbo-2024-04-09",
  "gpt-4-turbo-preview",
  "gpt-4-0125-preview",
  "gpt-4-1106-preview",
  "gpt-3.5-turbo-0125",
  "gpt-3.5-turbo",
  "gpt-3.5-turbo-1106",
  "claude-3-haiku-20240307",
  "claude-3-sonnet-20240229",
  "claude-3-opus-20240229",
  "claude-3-5-sonnet-20240620",
  "meta-llama/Llama-2-70b-chat-hf",
  "default"
] as const;

export type AiModel = (typeof AiModels)[number];

export type Question = {
  question: string;
  description: string;
  element: string | null;
  errorDetectedInHtml: boolean;
};

export type AnswerAction = {
  action_type: ApiActionType;
  id: string;
  args: Record<string, unknown>;
  config: Record<string, unknown> | null;
  description: string | null;
  query: string | null;
  status: ApiChatStatus;
  template: string | null;
  message: string;
  output: ApiActionOutput | null;
  streaming: boolean;
  visible: boolean;
  search_hits: ApiActionSearchHit[];
  search_chunks: ApiActionSearchChunk[];
  log_messages: ApiActionLogMessage[];
  prompt: string | null;
  timers: Record<string, number>;
};

export type AnswerQuestion = {
  query: string;
  actions: AnswerAction[];
  layout: FeedLayoutType;
  root: string;
  searchArgs: SearchArgs;
  status: ApiChatStatus;
  items: ICompositeResult[];
  flattenAction: FlatAction;
  stats: Record<string, number>;
  actionsRepoBranch: string | null;
  actionsRepoCommit: string | null;
  createdBy: string | null;
  createdAt: number | null;
  id?: string;
};

export type DebugModeUpdate = {
  answerId: string;
  questionIndex: number;
  enabled: boolean;
};

type SliceStateType = {
  aiModel: AiModel;
  answer: AnswerQuestion[];
  answerSteps: Record<string, unknown>[];
  answerIdentification: { data: Question[]; isLoading: boolean };
  selectedQuestion: AnswerQuestion | Question | null;
  selectedQuestionTitle: string | null;
  isAnswerLoading: boolean;
  isAiSearch: boolean;
  answerLoadingStartTimeMillis: number | null;
  showMoreButtonClicked: boolean;
  answerAttempt: number;
  answerCardVisibility: boolean;
  isAnswerLoaded: boolean;
  suggestedFollowupQuestions: NextStep[] | string[];
  isAnswerPaused: boolean;
  queryIndex: number;
  isDebugModeEnabledByQuestion: IsDebugModeEnabledByQuestion;
  answerBody: string[];
  answerSources: AnswerSources;
  lookupKey: string | null;
  chatHistory: ChatHistory[];
  selectedMcpServerId: number | null;
  // The quick action the next question starts, if it was started from a quick action chip.
  selectedQuickActionId: number | null;
};

const initialState: SliceStateType = {
  aiModel: "default",
  answer: [],
  answerSteps: [],
  answerIdentification: { data: [], isLoading: true },
  selectedQuestion: null,
  selectedQuestionTitle: null,
  isAnswerLoading: false,
  isAiSearch: false,
  answerLoadingStartTimeMillis: null,
  showMoreButtonClicked: false,
  answerAttempt: 0,
  answerCardVisibility: false,
  isAnswerLoaded: false,
  suggestedFollowupQuestions: [],
  isAnswerPaused: false,
  isDebugModeEnabledByQuestion: {} as IsDebugModeEnabledByQuestion,
  queryIndex: 0,
  answerBody: [],
  answerSources: [],
  lookupKey: null,
  chatHistory: [] as ChatHistory[],
  selectedMcpServerId: null,
  selectedQuickActionId: null
};

const answerGPT = createSlice({
  name: "answerGPT",
  initialState,
  reducers: {
    setAiModel: (state, action: PayloadAction<AiModel>) => {
      state.aiModel = action.payload;
    },
    updateAnswer: (state, action: PayloadAction<AnswerQuestion[]>) => {
      state.answer = action.payload;
    },
    updateAnswerSteps: (
      state,
      action: PayloadAction<Record<string, unknown>[]>
    ) => {
      state.answerSteps = action.payload;
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    updateAnswerIdentification: (state, action: PayloadAction<any | null>) => {
      state.answerIdentification = action.payload;
    },
    updateSelectedQuestion: (
      state,
      action: PayloadAction<{
        question: AnswerQuestion | Question | null;
        title: string;
      }>
    ) => {
      state.selectedQuestion = action.payload.question;
      state.selectedQuestionTitle = action.payload.title;
    },
    updateAnswerLoadingState: (state, action: PayloadAction<boolean>) => {
      state.isAnswerLoading = action.payload;
    },
    updateAnswerPaused: (state, action: PayloadAction<boolean>) => {
      state.isAnswerPaused = action.payload;
    },
    updateAnswerLoadingStartTimeMillis: (
      state,
      action: PayloadAction<number>
    ) => {
      state.answerLoadingStartTimeMillis = action.payload;
    },
    updateShowMoreButtonClicked: (state, action: PayloadAction<boolean>) => {
      state.showMoreButtonClicked = action.payload;
    },
    updateAnswerAttempt: (state, action: PayloadAction<number>) => {
      state.answerAttempt = action.payload;
    },
    updateAnswerCardVisibility: (state, action: PayloadAction<boolean>) => {
      state.answerCardVisibility = action.payload;
    },
    updateIsAnswerLoaded: (state, action: PayloadAction<boolean>) => {
      state.isAnswerLoaded = action.payload;
    },
    updateIsDebugModeEnabledByQuestion: (
      state,
      action: PayloadAction<DebugModeUpdate>
    ) => {
      const questions =
        state.isDebugModeEnabledByQuestion[action.payload.answerId] ?? {};
      questions[action.payload.questionIndex] = action.payload.enabled;
      state.isDebugModeEnabledByQuestion[action.payload.answerId] = questions;
    },
    updateSuggestedFollowupQuestions: (
      state,
      action: PayloadAction<NextStep[]>
    ) => {
      state.suggestedFollowupQuestions = action.payload;
    },
    resetGPTSearchStates: (state) => {
      state.answerIdentification = { data: [], isLoading: true };
      state.selectedQuestion = null;
      state.answerSteps = [];
    },
    updateQueryIndex: (state, action: PayloadAction<number>) => {
      state.queryIndex = action.payload;
    },
    updateAnswerBody: (state, action: PayloadAction<string>) => {
      state.answerBody.push(action.payload);
    },
    updateAnswerSources: (
      state,
      action: PayloadAction<ApiStreamingJSONAnswerSource[]>
    ) => {
      state.answerSources.push(action.payload);
    },
    updateLookupKey: (state, action: PayloadAction<string | null>) => {
      state.lookupKey = action.payload;
    },
    updateChatHistory: (state, action: PayloadAction<ChatHistory[]>) => {
      state.chatHistory = action.payload;
    },
    setSelectedQuickActionId: (state, action: PayloadAction<number | null>) => {
      state.selectedQuickActionId = action.payload;
    },
    setSelectedMcpServerId: (state, action: PayloadAction<number | null>) => {
      state.selectedMcpServerId = action.payload;
    }
  }
});

export const {
  setAiModel,
  updateAnswer,
  updateAnswerSteps,
  updateAnswerIdentification,
  updateSelectedQuestion,
  updateAnswerLoadingState,
  updateAnswerPaused,
  updateAnswerLoadingStartTimeMillis,
  updateShowMoreButtonClicked,
  updateAnswerAttempt,
  updateAnswerCardVisibility,
  updateIsAnswerLoaded,
  updateIsDebugModeEnabledByQuestion,
  updateSuggestedFollowupQuestions,
  resetGPTSearchStates,
  updateQueryIndex,
  updateAnswerSources,
  updateAnswerBody,
  updateLookupKey,
  updateChatHistory,
  setSelectedMcpServerId,
  setSelectedQuickActionId
} = answerGPT.actions;

export default answerGPT.reducer;
